import { useState, useRef, useEffect } from "react";
import { useCustom, useInvalidate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Modal,
  Select,
  Typography,
  Input,
  Descriptions,
  Divider,
  notification,
  Empty,
  Result,
  Button,
  Skeleton,
  theme,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  WorkflowHistoryReport,
  type VoteClickData,
} from "../WorkflowHistoryReport";
import { useWorkflowHistory } from "../../hooks/useWorkflowHistory";
import { axiosInstance } from "../../providers/dataProvider";
import type { ActivityPath } from "../../types";

const { Text, Title } = Typography;
const { TextArea } = Input;

/** Post-vote retry strategy configuration */
const VOTE_RETRY = {
  /** Maximum number of retry attempts after voting */
  maxRetries: 3,
  /** Base delay in ms before first retry */
  baseDelayMs: 1500,
  /** Additional delay per attempt in ms (delay = baseDelayMs + attempt * incrementMs) */
  incrementMs: 1000,
  /** Delay before invalidating caches to let Aras process side-effects */
  cacheInvalidationDelayMs: 2000,
} as const;

interface WorkflowHistoryWithVotingProps {
  /** The Aras ItemType name (e.g., "Lead", "Opportunity", "Customer") */
  itemType: string;
  /** The item ID */
  itemId: string;
  /** Display name for the item (shown in voting modal) */
  itemDisplayName?: string;
  /** Enable fetching data (default: true) */
  enabled?: boolean;
  /**
   * Whether to show the Card wrapper with title.
   * Set to false when used inside another Card (e.g., RelationshipTabs).
   * Default: true
   */
  showTitle?: boolean;
  /** Callback invoked after a vote is successfully completed */
  onVoteComplete?: () => void;
}

/**
 * A self-contained component that displays the Workflow History Report
 * with integrated voting modal functionality.
 *
 * Usage:
 * ```tsx
 * {item?.id && (
 *   <WorkflowHistoryWithVoting
 *     itemType="Lead"
 *     itemId={item.id}
 *     itemDisplayName={item.itemNumber || item.name}
 *   />
 * )}
 * ```
 */
export const WorkflowHistoryWithVoting: React.FC<
  WorkflowHistoryWithVotingProps
> = ({
  itemType,
  itemId,
  itemDisplayName,
  enabled = true,
  showTitle = true,
  onVoteComplete,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { token } = theme.useToken();

  // Voting modal state
  const [voteModalVisible, setVoteModalVisible] = useState(false);
  const [selectedVoteData, setSelectedVoteData] =
    useState<VoteClickData | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [voteComments, setVoteComments] = useState("");
  const [completing, setCompleting] = useState(false);

  // Track whether a vote was just completed (useState so it persists across re-renders)
  const [justVoted, setJustVoted] = useState(false);
  // Track if we're waiting for workflow data to refresh after a vote
  const [refreshingAfterVote, setRefreshingAfterVote] = useState(false);

  // Workflow history hook for refetching after vote
  const {
    workflowHistory,
    isLoading,
    refetch: refetchWorkflowHistory,
  } = useWorkflowHistory({
    itemType,
    itemId,
    enabled,
  });

  // Fetch activity paths when voting modal is open
  const { data: pathsData, isLoading: pathsLoading } = useCustom<
    ActivityPath[]
  >({
    url: `/workflow/activities/${selectedVoteData?.activityId}/paths`,
    method: "get",
    queryOptions: {
      enabled: !!selectedVoteData?.activityId && voteModalVisible,
    },
  });

  const paths = pathsData?.data || [];

  // Track retry attempts for post-vote refetch
  const retryCountRef = useRef(0);

  // When refreshingAfterVote is true, schedule retries with increasing delay
  useEffect(() => {
    if (!refreshingAfterVote) {
      retryCountRef.current = 0;
      return;
    }

    const attempt = retryCountRef.current;
    if (attempt >= VOTE_RETRY.maxRetries) {
      setRefreshingAfterVote(false);
      return;
    }

    const delay = VOTE_RETRY.baseDelayMs + attempt * VOTE_RETRY.incrementMs;
    const timer = setTimeout(() => {
      retryCountRef.current += 1;
      refetchWorkflowHistory();
    }, delay);

    return () => clearTimeout(timer);
  }, [refreshingAfterVote, refetchWorkflowHistory]);

  // Stop refreshing when workflow data arrives after a vote
  useEffect(() => {
    if (refreshingAfterVote && workflowHistory && !isLoading) {
      setRefreshingAfterVote(false);
    }
  }, [refreshingAfterVote, workflowHistory, isLoading]);

  // Show empty state if there's no workflow data (after loading completes)
  // NOTE: This must be placed AFTER all hooks to comply with React's rules of hooks
  // ERP-529: Always show tab content, display empty state if no workflow history
  if (refreshingAfterVote || (isLoading && justVoted)) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  if (!isLoading && !workflowHistory) {
    // After a vote, the item may have transitioned to a new lifecycle state
    // where the current user no longer has access. Show a helpful message.
    if (justVoted) {
      return (
        <Result
          status="success"
          title={t("workflow.voteCompletedTitle", "Duyệt thành công")}
          subTitle={t(
            "workflow.voteCompletedSubtitle",
            "Mục đã được chuyển sang trạng thái mới. Bạn có thể không còn quyền truy cập mục này.",
          )}
          extra={
            <Button
              type="primary"
              onClick={() => {
                setJustVoted(false);
                navigate(`/${itemType}`);
              }}
            >
              {t("workflow.backToList", "Quay về danh sách")}
            </Button>
          }
          style={{ padding: "40px 0" }}
        />
      );
    }
    return (
      <Empty
        description={t(
          "workflow.noWorkflowHistory",
          "Chưa có quy trình được thiết lập cho mục này",
        )}
        style={{ padding: "40px 0" }}
      />
    );
  }

  // Handle voting on workflow activity
  const handleVoteClick = (data: VoteClickData) => {
    setSelectedVoteData(data);
    setVoteModalVisible(true);
  };

  const handleCloseVoteModal = () => {
    setVoteModalVisible(false);
    setSelectedVoteData(null);
    setSelectedPath(null);
    setVoteComments("");
  };

  const handleCompleteVote = async () => {
    if (!selectedVoteData?.activityId || !selectedPath) return;

    // Find the selected path object to get all required fields
    const pathObj = paths.find((p) => p.id === selectedPath);
    if (!pathObj) {
      notification.error({
        message: t("workflow.pathNotFound"),
        description: t(
          "workflow.pathNotFoundDescription",
          "Không tìm thấy đường dẫn workflow. Vui lòng thử lại.",
        ),
        placement: "topRight",
        duration: 5,
      });
      return;
    }

    setCompleting(true);
    try {
      const response = await axiosInstance.post("workflow/complete", {
        activityId: pathObj.activityId || selectedVoteData.activityId,
        activityAssignmentId:
          pathObj.activityAssignmentId || selectedVoteData.assignmentId || "",
        pathId: selectedPath,
        pathName: pathObj.name || pathObj.label || "",
        comments: voteComments || undefined,
      });
      const successMessage = response?.data?.message;
      notification.success({
        message: t("workflow.taskCompleted"),
        description:
          successMessage ||
          t(
            "workflow.taskCompletedDescription",
            "Công việc đã được duyệt và chuyển sang bước tiếp theo.",
          ),
        placement: "topRight",
        duration: 5,
      });
      handleCloseVoteModal();

      // Mark that a vote was just completed so the empty state
      // can show a helpful message if the item is no longer accessible
      setJustVoted(true);
      setRefreshingAfterVote(true);

      // Invalidate all related caches so list/detail views stay in sync
      // Delay cache invalidation to let Aras finish processing lifecycle side-effects
      setTimeout(() => {
        invalidate({
          resource: itemType,
          invalidates: ["list", "detail"],
        });

        // Notify parent to refresh lifecycle/workflow data
        onVoteComplete?.();
      }, VOTE_RETRY.cacheInvalidationDelayMs);
    } catch (error: unknown) {
      notification.error({
        message: t("workflow.taskFailed"),
        description:
          (error as Error)?.message ||
          t(
            "workflow.taskFailedDescription",
            "Có lỗi xảy ra khi duyệt công việc. Vui lòng thử lại.",
          ),
        placement: "topRight",
        duration: 8,
      });
    } finally {
      setCompleting(false);
    }
  };

  // Workflow History Report component
  const workflowReport = (
    <WorkflowHistoryReport
      itemType={itemType}
      itemId={itemId}
      enabled={enabled}
      onVoteClick={handleVoteClick}
      showTitle={showTitle}
    />
  );

  return (
    <>
      {/* Workflow History Report */}
      {showTitle ? (
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>{workflowReport}</Col>
        </Row>
      ) : (
        workflowReport
      )}

      {/* Voting Modal */}
      <Modal
        title={
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            {t("workflow.activityCompletion")}
          </Title>
        }
        open={voteModalVisible}
        onOk={handleCompleteVote}
        onCancel={handleCloseVoteModal}
        confirmLoading={completing}
        okButtonProps={{ disabled: !selectedPath }}
        okText={t("actions.complete")}
        cancelText={t("actions.cancel")}
        width={520}
      >
        {/* Workflow Info Section */}
        <Descriptions
          column={1}
          size="small"
          style={{ marginBottom: 16 }}
          labelStyle={{ fontWeight: 600, width: 120 }}
        >
          <Descriptions.Item label={t("workflow.workflowName")}>
            <Text strong style={{ color: token.colorPrimary }}>
              {itemDisplayName || workflowHistory?.itemName || "-"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label={t("workflow.activity")}>
            {selectedVoteData?.activityName || "-"}
          </Descriptions.Item>
          {selectedVoteData?.assignedTo && (
            <Descriptions.Item label={t("table.assignedTo")}>
              {selectedVoteData.assignedTo}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Instructions Section */}
        {selectedVoteData?.instructions && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("table.instructions")}:</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  backgroundColor: token.colorFillQuaternary,
                  borderRadius: 4,
                  borderLeft: `3px solid ${token.colorPrimary}`,
                }}
              >
                <Text>{selectedVoteData.instructions}</Text>
              </div>
            </div>
          </>
        )}

        <Divider style={{ margin: "12px 0" }} />

        {/* Vote Section */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("workflow.vote")}:
          </Text>
          <Select
            style={{ width: "100%" }}
            placeholder={t("workflow.selectPathPlaceholder")}
            value={selectedPath}
            onChange={setSelectedPath}
            loading={pathsLoading}
            options={paths.map((path) => ({
              label: path.label || path.name,
              value: path.id,
            }))}
          />
        </div>

        {/* Comments Section */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("workflow.comments")}:
          </Text>
          <TextArea
            rows={3}
            value={voteComments}
            onChange={(e) => setVoteComments(e.target.value)}
            placeholder={t("workflow.commentsPlaceholder")}
          />
        </div>
      </Modal>
    </>
  );
};

export default WorkflowHistoryWithVoting;
